<template>
  <div
    class="site blog-site"
    :style="siteStyle"
    @mousedown="pressGlass"
    @mouseleave="resetGlass"
    @mousemove="moveGlass"
    @mouseout="leaveGlass"
    @mouseup="releaseGlass"
  >
    <header class="topbar">
      <router-link class="site-logo-link" to="/" :aria-label="`${siteSettings.siteName}首页`">
        <img class="site-logo" :src="siteSettings.logoUrl" :alt="siteSettings.siteName">
      </router-link>
      <nav class="nav-links" aria-label="前台导航">
        <router-link v-for="menu in menus" :key="menu.id" :to="menu.path">
          {{ menu.name }}
        </router-link>
      </nav>
      <div
        v-if="weatherText"
        class="nav-weather"
        :title="weatherTitle"
        aria-label="当前位置天气"
      >
        <span class="nav-weather-icon">{{ weatherIcon }}</span>
        <span class="nav-weather-location">{{ weather.location }}</span>
        <span class="nav-weather-temp">{{ weather.temperature }}</span>
      </div>
      <div class="glass-opacity-control" aria-label="全局玻璃透明度">
        <span class="glass-opacity-icon el-icon-magic-stick" aria-hidden="true"></span>
        <el-slider
          v-model="glassOpacity"
          :format-tooltip="formatGlassTooltip"
          :max="100"
          :min="0"
          :show-input="false"
          class="glass-opacity-slider"
        />
        <span class="glass-opacity-value">{{ glassOpacity }}%</span>
      </div>
    </header>

    <main>
      <router-view :site-settings="siteSettings" />
    </main>

    <footer class="site-footer">
      <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">苏ICP备2026046764号</a>
    </footer>
  </div>
</template>

<script>
import { fetchPublicMenus } from '@/services/menus';
import {
  DEFAULT_SITE_SETTINGS,
  fetchPublicSiteSettings,
  normalizeSiteSettings,
} from '@/services/siteSettings';
import { fetchPublicWeather } from '@/services/weather';

const GLASS_OPACITY_KEY = 'site:glass-opacity';
const DEFAULT_GLASS_OPACITY = 58;
const GLASS_TARGET_SELECTOR = [
  '.topbar',
  '.hero-panel',
  '.article-list-hero',
  '.article-search',
  '.blog-pagination',
  '.post-card',
  '.skill-list-item',
  '.skill-detail',
  '.article-header',
  '.article-body',
  '.glass-panel',
  '.nav-weather',
  '.site-footer a',
].join(',');

function normalizeOpacity(value) {
  const opacity = Number(value);

  if (!Number.isFinite(opacity)) {
    return DEFAULT_GLASS_OPACITY;
  }

  return Math.min(100, Math.max(0, Math.round(opacity)));
}

export default {
  name: 'BlogLayout',
  data() {
    return {
      glassOpacity: DEFAULT_GLASS_OPACITY,
      menus: [],
      siteSettings: { ...DEFAULT_SITE_SETTINGS },
      weather: null,
    };
  },
  computed: {
    siteStyle() {
      const backgroundImage = this.$route.meta?.blogBackgroundImage || this.siteSettings.backgroundImageUrl;

      if (!backgroundImage) {
        return {};
      }

      return {
        '--blog-background-image': `url("${backgroundImage}")`,
      };
    },
    weatherText() {
      return this.weather?.text || '';
    },
    weatherIcon() {
      return this.weather?.condition || '☁️';
    },
    weatherTitle() {
      if (!this.weatherText) {
        return '';
      }

      const details = [
        this.weather?.humidity ? `湿度 ${this.weather.humidity}` : '',
        this.weather?.wind ? `风速 ${this.weather.wind}` : '',
        this.weather?.updatedAt ? `更新于 ${this.weather.updatedAt}` : '',
      ].filter(Boolean);

      return details.length ? `${this.weatherText}｜${details.join('｜')}` : this.weatherText;
    },
  },
  watch: {
    glassOpacity(value) {
      this.applyGlassOpacity(value);
    },
    siteSettings: {
      deep: true,
      handler() {
        this.applyDocumentBranding();
      },
    },
  },
  mounted() {
    const savedOpacity = window.localStorage.getItem(GLASS_OPACITY_KEY);

    this.glassOpacity = normalizeOpacity(savedOpacity ?? DEFAULT_GLASS_OPACITY);
    this.applyGlassOpacity(this.glassOpacity);
    this.applyDocumentBranding();
    this.loadSiteSettings();
    this.loadMenus();
    this.loadWeather();
  },
  methods: {
    async loadSiteSettings() {
      try {
        const response = await fetchPublicSiteSettings();
        this.siteSettings = normalizeSiteSettings(response.data?.data ?? response.data);
      } catch (error) {
        this.siteSettings = { ...DEFAULT_SITE_SETTINGS };
      }
    },
    applyDocumentBranding() {
      const siteName = this.siteSettings.siteName || DEFAULT_SITE_SETTINGS.siteName;
      const logoUrl = this.siteSettings.logoUrl || DEFAULT_SITE_SETTINGS.logoUrl;
      const iconType = this.faviconType(logoUrl);

      document.title = siteName;
      document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]').forEach((link) => {
        link.setAttribute('href', logoUrl);
        if (iconType) {
          link.setAttribute('type', iconType);
        } else {
          link.removeAttribute('type');
        }
      });
    },
    faviconType(url) {
      const normalizedUrl = String(url || '').split('?')[0].toLowerCase();

      if (normalizedUrl.endsWith('.svg')) {
        return 'image/svg+xml';
      }
      if (normalizedUrl.endsWith('.png')) {
        return 'image/png';
      }
      if (normalizedUrl.endsWith('.jpg') || normalizedUrl.endsWith('.jpeg')) {
        return 'image/jpeg';
      }
      if (normalizedUrl.endsWith('.webp')) {
        return 'image/webp';
      }
      return '';
    },
    async loadMenus() {
      try {
        const response = await fetchPublicMenus();
        const body = response.data;
        this.menus = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
      } catch (error) {
        this.menus = [];
      }
    },
    async loadWeather() {
      try {
        const response = await fetchPublicWeather();
        const body = response.data;
        const data = body?.data || body;

        this.weather = data?.text ? data : null;
      } catch (error) {
        this.weather = null;
      }
    },
    findGlassPanel(event) {
      return event.target?.closest?.(GLASS_TARGET_SELECTOR);
    },
    applyGlassOpacity(value) {
      const percentage = normalizeOpacity(value);
      const opacity = percentage / 100;
      const root = document.documentElement;

      root.style.setProperty('--global-glass-opacity', opacity.toFixed(2));
      root.style.setProperty('--global-glass-card-opacity', Math.min(1, opacity * 0.92).toFixed(2));
      root.style.setProperty('--global-glass-sheen-opacity', Math.min(1, opacity * 0.74).toFixed(2));
      root.style.setProperty('--global-glass-hover-opacity', Math.min(1, opacity + 0.2).toFixed(2));
      root.style.setProperty('--global-glass-surface-opacity', (0.06 + opacity * 0.24).toFixed(2));
      root.style.setProperty('--global-glass-surface-strong-opacity', (0.14 + opacity * 0.36).toFixed(2));
      root.style.setProperty('--global-glass-nav-start-opacity', (0.08 + opacity * 0.32).toFixed(2));
      root.style.setProperty('--global-glass-nav-mid-opacity', (0.04 + opacity * 0.16).toFixed(2));
      root.style.setProperty('--global-glass-nav-end-opacity', (0.08 + opacity * 0.22).toFixed(2));
      root.style.setProperty('--global-glass-admin-start-opacity', (0.24 + opacity * 0.36).toFixed(2));
      root.style.setProperty('--global-glass-admin-mid-opacity', (0.18 + opacity * 0.34).toFixed(2));
      root.style.setProperty('--global-glass-admin-end-opacity', (0.2 + opacity * 0.32).toFixed(2));

      window.localStorage.setItem(GLASS_OPACITY_KEY, String(percentage));
    },
    formatGlassTooltip(value) {
      return `玻璃透明度 ${value}%`;
    },
    moveGlass(event) {
      const panel = this.findGlassPanel(event);

      if (!panel) {
        return;
      }

      const rect = panel.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;
      const offsetX = x - 0.5;
      const offsetY = y - 0.5;

      panel.style.setProperty('--glass-x', `${Math.round(x * 100)}%`);
      panel.style.setProperty('--glass-y', `${Math.round(y * 100)}%`);
      panel.style.setProperty('--glass-shift-x', `${(offsetX * 8).toFixed(2)}px`);
      panel.style.setProperty('--glass-shift-y', `${(offsetY * 8).toFixed(2)}px`);
      panel.style.setProperty('--glass-tilt-x', `${(-offsetY * 5).toFixed(2)}deg`);
      panel.style.setProperty('--glass-tilt-y', `${(offsetX * 6).toFixed(2)}deg`);
      panel.style.setProperty('--glass-glow-opacity', 'var(--global-glass-hover-opacity)');
    },
    pressGlass(event) {
      const panel = this.findGlassPanel(event);

      if (panel) {
        panel.classList.add('is-pressed');
      }
    },
    releaseGlass(event) {
      const panel = this.findGlassPanel(event);

      if (panel) {
        panel.classList.remove('is-pressed');
      }
    },
    resetPanel(panel) {
      if (!panel) {
        return;
      }

      panel.classList.remove('is-pressed');
      panel.style.setProperty('--glass-shift-x', '0px');
      panel.style.setProperty('--glass-shift-y', '0px');
      panel.style.setProperty('--glass-tilt-x', '0deg');
      panel.style.setProperty('--glass-tilt-y', '0deg');
      panel.style.setProperty('--glass-glow-opacity', 'var(--global-glass-opacity)');
    },
    leaveGlass(event) {
      const panel = this.findGlassPanel(event);

      if (!panel || panel.contains(event.relatedTarget)) {
        return;
      }

      this.resetPanel(panel);
    },
    resetGlass(event) {
      this.resetPanel(this.findGlassPanel(event));
    },
  },
};
</script>
